import {
  FieldRichText,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import type { Queue } from 'lib-shared-legacy-types';
import { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { WidgetPreview } from 'components/conversation/components/widgetPreview';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import SelectField from 'components/shared/util/SelectField';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import type { QueueModalStatePropsT } from '../../containers/modals/queue';
import type {
  FlowChatNodeQueuePayloadT,
  FlowChatNodeQueueT,
} from '../nodeTypes';

type QueueModalFormStatePropsT = {
  queueValue: string;
  messageValue: string;
};

type PropsT = FlowManagerEditModalComponentT<FlowChatNodeQueueT> &
  QueueModalFormStatePropsT &
  QueueModalStatePropsT;

type InjectedProps = InjectedFormProps<QueueModalFormValuesT, PropsT>;

export type QueueModalFormValuesT = {
  message: string;
} & FlowChatNodeQueueT;

const FORM_NAME = 'chatFlowQueueForm';

const validate = (values: FlowChatNodeQueueT): FormErrors => {
  let errors = {};

  if (!values) {
    return errors;
  }

  if (!values.payload || !values.payload.queue || !values.payload.queue.value) {
    errors = {
      payload: {
        queue: {
          value: 'Required - please select a queue',
        },
      },
    };
  }

  return errors;
};

function stripTagsAndTrim(value: string): string {
  const tempDiv = document.createElement('div');
  // TODO: [TEAMS-12081] Investigate improper use of innerHTML
  tempDiv.innerHTML = value;
  const textContent = tempDiv?.textContent?.trim();
  return textContent || '';
}

class QueueModal extends PureComponent<PropsT & InjectedProps> {
  @boundMethod
  handleSaveClick(values: QueueModalFormValuesT) {
    let selectedQueue;
    if (values.payload?.queue?.value) {
      const matchingQueue = this.props.queuesState.find(
        (queue: Queue) => queue.id === values.payload?.queue?.value,
      );

      if (matchingQueue) {
        selectedQueue = {
          value: matchingQueue.id,
          label: matchingQueue.name,
        };
      }
    }

    const trimmedMessage = values.message && stripTagsAndTrim(values.message);
    const payload: FlowChatNodeQueuePayloadT = {
      queue: selectedQueue,
      welcome_message: trimmedMessage ? values.message : null,
    };

    const nodeOptionsText = `Queue ${
      selectedQueue.label ? `(${selectedQueue.label})` : ''
    }`;

    const nodeOptions: FlowChatNodeQueueT = {
      ...this.props.node,
      ...values,
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.QUEUE,
      overlay: [],
      payload,
      text: nodeOptionsText,
    };

    this.props.replaceNode(nodeOptions);
    this.props.triggerClose();
  }

  @boundMethod
  closeModal() {
    const { destroy, triggerClose } = this.props;
    destroy();
    triggerClose();
  }

  render() {
    const {
      handleSubmit,
      queueValue,
      messageValue,
      node,
      nodeLabel,
      queuesState,
    } = this.props;
    const { payload } = node;

    const listQueues = (queue: Queue) => {
      return (
        <option key={queue.id} value={queue.id}>
          {queue.name}
        </option>
      );
    };

    const isChanged =
      queueValue !== payload?.queue?.value ||
      stripTagsAndTrim(messageValue) !==
        stripTagsAndTrim(payload?.welcome_message || '');

    return (
      <div className="conversational-modal queue modal-chatflow-holder">
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalHead title={nodeLabel} onClose={this.closeModal}>
            A queue is where a conversation is queued until an available agent
            can support the customer.
          </ModalHead>
          <ModalBody>
            <div className="conversational-modal__columns">
              <Field
                displayName="Queue"
                name="payload.queue.value"
                className="conversational-modal__field"
                component={SelectField}
              >
                <option hidden value="">
                  {' '}
                  - Please select a queue
                </option>
                {queuesState.map(listQueues)}
              </Field>
            </div>

            <Fragment>
              <p className="conversational-modal__text">
                Here you can write the message that will welcome the user to the
                chat with your agent.
                <strong>
                  {' '}
                  Remember to animate the user to start typing a message to your
                  agent in order to start the chat.
                </strong>{' '}
                If you leave this message blank, the user will see “Welcome! How
                can we help you?” in the languages of your widget customization
              </p>

              <div className="conversational-modal__columns">
                <div className="conversational-modal__column">
                  <WidgetPreview
                    message={messageValue}
                    className="conversational-modal__preview"
                  />
                </div>
                <div className="conversational-modal__column">
                  <Field
                    component={FieldRichText}
                    name="message"
                    className="conversational-modal__editor"
                    label="Your message"
                  />
                </div>
              </div>
            </Fragment>
          </ModalBody>

          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            confirmProps={{ disabled: !isChanged }}
            cancelOnClick={this.closeModal}
          />
        </Form>
      </div>
    );
  }
}

const ChatFlowQueueForm = reduxForm<QueueModalFormValuesT, PropsT>({
  form: FORM_NAME, // a unique identifier for this form
  enableReinitialize: true,
  validate,
})(QueueModal);

export default connect(
  (state: RootReducerStateT): QueueModalFormStatePropsT => {
    const form = state.form[FORM_NAME];

    return {
      queueValue: form?.values ? form.values.payload?.queue?.value : '',
      messageValue: form?.values ? form.values.message : '',
    };
  },
)(ChatFlowQueueForm);
